import dayjs from 'dayjs'

import { SystemField } from 'common/audience/components/MergeModal/steps/Preview/PreviewModalFields/usePreviewFields'
import {
  UserFieldsDateFormula,
  UserFieldsDateTimeFormula,
} from 'common/fields/constants/UserFieldsTypes'
import { FieldType } from 'common/fields/entities/enums'
import { Field } from 'common/fields/entities/field'
import { DATE_FORMULA, DATE_TIME_FORMULA } from 'common/filter/models/AudienceFilter/constants'
import { formatDateTime, parseDateTimeWithBotTimezoneFromBackend } from 'common/usesList/utils'
import { VAR_REGEX, SPLIT_REGEX } from 'constants/RegExs'

const boolTextMap: Record<string, string> = {
  false: 'No',
  true: 'Yes',
}

const formatArray = (arrayLength: number): string => {
  if (arrayLength === 0) {
    return 'no elements'
  }

  if (arrayLength === 1) {
    return '1 element'
  }

  return arrayLength + ' elements'
}

export const getFieldValue = (
  fieldType: FieldType,
  actionValue: string | Array<string | number>,
  fields: Field[] | SystemField[],
  utcOffset: number | string | null = null,
  botTimezone: string | null = null,
): string | null | undefined => {
  if (typeof actionValue !== 'object') {
    switch (fieldType) {
      case FieldType.ARRAY:
        return formatArray(actionValue.length)
      case FieldType.TEXT:
      case FieldType.NUMBER: {
        if (actionValue === null) {
          return actionValue
        }

        if (!Number.isNaN(Number(actionValue))) {
          return actionValue
        }

        const actionValueParts = actionValue.split(SPLIT_REGEX)

        if (actionValueParts.length === 1) {
          return actionValue
        }

        const actionValueFields = actionValueParts.map((a) => VAR_REGEX.exec(a)).filter((a) => a)

        let newActionValue = actionValue
        for (const part of actionValueFields) {
          if (part === null) {
            return newActionValue
          }
          const fieldId = part[1]
          const fieldVariable = part[0]

          const current = fields.find((f) => ('id' in f ? f.id === fieldId : false))
          const replacementString = current?.caption || 'Invalid Variable'
          newActionValue = newActionValue.replace(fieldVariable, `{${replacementString}}`)
        }

        return newActionValue
      }
      case FieldType.BOOLEAN:
        return boolTextMap[actionValue]
      case FieldType.DATE: {
        if (actionValue === UserFieldsDateFormula.NOW) {
          return 'the date of the action'
        }

        if (actionValue.includes(UserFieldsDateFormula.NOW)) {
          return 'the date of the action'
        }

        if (actionValue.includes(DATE_FORMULA)) {
          const parsedValue = VAR_REGEX.exec(actionValue)
          VAR_REGEX.lastIndex = 0
          if (parsedValue) {
            const fieldFullId = parsedValue[1]
            return fields.find((f) => ('id' in f ? f.id === fieldFullId : false))?.caption
          }
        }

        return formatDateTime(actionValue, true)
      }
      case FieldType.DATETIME: {
        if (actionValue === UserFieldsDateTimeFormula.NOW) {
          return 'the date/time of the action'
        }

        if (actionValue.includes(UserFieldsDateTimeFormula.NOW)) {
          return 'the date/time of the action'
        }

        if (actionValue.includes(DATE_TIME_FORMULA)) {
          const parsedValue = VAR_REGEX.exec(actionValue)
          VAR_REGEX.lastIndex = 0
          if (parsedValue) {
            const fieldFullId = parsedValue[1]
            return fields.find((f) => ('id' in f ? f.id === fieldFullId : false))?.caption
          }
        }

        if (utcOffset !== null) {
          let validTimezone: string | number
          try {
            // @ts-expect-error should catch if it's not a string
            validTimezone = utcOffset.match(/[+-]+\d\d|\d\d/)?.[0] || '0'
          } catch (e) {
            validTimezone = utcOffset
          }
          return formatDateTime(dayjs(actionValue).utcOffset(Number(validTimezone)))
        }

        const utcByBotTimezone =
          botTimezone !== null
            ? dayjs(actionValue).tz(botTimezone).format('Z')
            : dayjs(actionValue).format('Z')
        return dayjs(parseDateTimeWithBotTimezoneFromBackend(actionValue, botTimezone)).format(
          `D MMM YYYY, HH:mm (UTC ${utcByBotTimezone})`,
        )
      }
    }
  }
  switch (fieldType) {
    case FieldType.ARRAY:
      return formatArray(actionValue.length)
  }
  return ''
}
